import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import type { PaymentError } from "@/components/checkout/InlinePayment";

/**
 * Task #4687 — in-modal payment-error banner.
 *
 * The banner adapts its framing to the error's classification so the renter is
 * not falsely alarmed:
 *  - "charge_failed" → a genuine charge problem (decline, SCA failure, server
 *    error). Keeps the alarming red "Payment failed" header.
 *  - "card_validation" → a client-side card-FIELD problem reported by
 *    elements.submit() (e.g. an incomplete card number after the Stripe
 *    Elements were remounted by a moderation block). Uses a calm amber
 *    "check your card details" header — the charge was never attempted, so
 *    "Payment failed" would be misleading.
 *
 * Extracted from CheckoutPage so the classification → framing mapping can be
 * unit-tested in isolation.
 */
export function CheckoutPaymentErrorBanner({
  error,
  onDismiss,
  onCheckStatus,
}: {
  error: PaymentError;
  onDismiss: () => void;
  onCheckStatus?: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const isCardValidation = error.kind !== "charge_failed";

  // Calm amber for validation or an unknown status; red only for charge failure.
  const containerClass = isCardValidation
    ? "mb-4 flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-50 p-3 text-sm text-amber-900"
    : "mb-4 flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive";
  const bodyClass = isCardValidation ? "mt-1 text-amber-800" : "mt-1 text-destructive/90";
  const buttonClass = isCardValidation
    ? "shrink-0 rounded-full border border-amber-500/40 px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100"
    : "shrink-0 rounded-full border border-destructive/40 px-3 py-1 text-xs font-semibold text-destructive hover:bg-destructive/10";

  const heading = error.kind === "status_unavailable"
    ? t("checkout.status_unavailable_heading", "Payment status unavailable")
    : error.kind === "location_verification"
    ? t("checkout.confirm_pickup_location_heading", "Confirm your pickup location")
    : isCardValidation
      ? t("checkout.check_card_details_heading", "Check your card details")
      : t("checkout.payment_failed", "Payment failed");

  return (
    <div
      data-testid="booking-modal-payment-error"
      data-error-kind={error.kind}
      // card_validation is a calm, pre-charge field hint → role="status"
      // (polite). A genuine charge failure stays role="alert" (assertive) so
      // screen readers interrupt with the urgent outcome.
      role={isCardValidation ? "status" : "alert"}
      aria-live={isCardValidation ? "polite" : "assertive"}
      className={containerClass}
    >
      <div className="flex-1">
        <p className="font-semibold">{heading}</p>
        <p className={bodyClass}>{error.message}</p>
      </div>
      {error.kind === "status_unavailable" && onCheckStatus ? (
        <button type="button" onClick={() => void onCheckStatus()} className={buttonClass}>
          {t("checkout.check_booking_status", "Check booking status")}
        </button>
      ) : error.kind === "status_unavailable" ? (
        <Link href="/trips" className={buttonClass}>
          {t("checkout.check_existing_bookings", "Check my bookings")}
        </Link>
      ) : (
        <button type="button" onClick={onDismiss} className={buttonClass}>
          {t("common.retry", "Try again")}
        </button>
      )}
    </div>
  );
}
